import {
  EyeOutlined,
  EditOutlined,
  QuestionCircleOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { getLabData } from '@app/api/labs.api';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { BaseTable } from '@app/components/common/BaseTable/BaseTable';
import { BaseButtonsForm } from '@app/components/common/forms/BaseButtonsForm/BaseButtonsForm';
import FilterComponent from '@app/components/TableFilter/TableFilter';
import { notificationController } from '@app/controllers/notificationController';
import { useMounted } from '@app/hooks/useMounted';
import { DepartmentTableRow } from '@app/models/departments/DepartmentResponse';
import { LabTableRow } from '@app/models/labs/LabResponse';
import { Pagination } from '@app/models/Pagination';
import { MainlabTaskStatusColors, MainlabTaskStatusLabels } from '@app/utils/labItemUtilils';
import { TablePaginationConfig, Space, Tooltip, Button, Popconfirm, Input, Tag } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const initialPagination: Pagination = {
  current: 1,
  pageSize: 5,
  total: 10,
};

const LabTaskTable = () => {
  const [filters, setFilters] = useState<{ [key: string]: string | [string, string] | null }>({});
  const [statusOptions, setStatusOptions] = useState<{ label: string; value: string }[] | null>(null);
  const [tableData, setTableData] = useState<{ data: LabTableRow[]; pagination: Pagination; loading: boolean }>({
    data: [],
    pagination: initialPagination,
    loading: false,
  });
  const { isMounted } = useMounted();
  const navigate = useNavigate();
  const [form] = BaseButtonsForm.useForm();
  const lastPathnameRef = useRef<string | null>(null);

  const location = useLocation();

  const fetch = useCallback(
    (pagination = { current: 1, pageSize: 10 }, activeFilters = filters) => {
      setTableData((prev) => ({ ...prev, loading: true }));

      const storedType = localStorage.getItem('selected_lab_type') || 'REGULAR_TEST';
      const cleanFilters = sanitizeFilters(activeFilters);

      getLabData({ ...pagination, ...cleanFilters, type: storedType })
        .then((res) => {
          setTableData({
            data: res.data || [],
            pagination: res.pagination,
            loading: false,
          });
          setStatusOptions(res.status_options);
        })
        .catch((error) => {
          notificationController.error({ message: error.message });
          setTableData((prev) => ({ ...prev, loading: false }));
        });
    },
    [filters],
  );

  const sanitizeFilters = (filters: Record<string, any>) => {
    const cleanFilters: Record<string, any> = {};
    for (const key in filters) {
      if (filters[key] !== null && filters[key] !== '') {
        cleanFilters[key] = filters[key];
      }
    }
    return cleanFilters;
  };

  useEffect(() => {
    if (lastPathnameRef.current !== location.pathname) {
      lastPathnameRef.current = location.pathname;
      fetch();
    }
  }, [location.pathname]);

  const handleTableChange = (pagination: TablePaginationConfig) => {
    fetch(
      {
        current: pagination.current || 1,
        pageSize: pagination.pageSize || 5,
      },
      filters,
    );
  };

  const handleViewRow = (id: number) => {
    navigate(`/view-lab-task`, { state: { id: id } });
  };

  const showTotal = (total: number, range: [number, number]): React.ReactNode => {
    return (
      <span
        style={{ fontSize: '14px', marginRight: '20px', color: '#adadad' }}
      >{`Showing ${range[0]} - ${range[1]} of ${total} Lab Tasks`}</span>
    );
  };

  const searchData: {
    name: string;
    placeholder: string;
    type: 'text' | 'dropdown';
    options?: Record<string, { label: string; value: string }[]>;
  }[] = [
    { name: 'search', placeholder: 'Search', type: 'text' },
    {
      name: 'status',
      placeholder: 'status',
      type: 'dropdown',
      options: {
        statusOptions: statusOptions || [],
      },
    },
  ];

  const columns: ColumnsType<DepartmentTableRow> = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      width: '30%',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const label = MainlabTaskStatusLabels[status] || status;
        const color = MainlabTaskStatusColors[status] || '#595959';

        return <span style={{ color, fontWeight: 500 }}>{label}</span>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '30%',
      render: (_, record) => {
        return (
          <Space>
            <Tooltip title="View">
              <Button
                type="text"
                onClick={() => handleViewRow(record.id)}
                icon={<EyeOutlined />}
                style={{ color: '#1890ff' }}
              />
            </Tooltip>
          </Space>
        );
      },
    },
  ];
  return (
    <BaseCard id="basic-table" title="Labs & Research" padding="1.25rem">
      <FilterComponent
        data={searchData}
        onFilter={(newFilters) => {
          setFilters(newFilters);
          fetch({ current: 1, pageSize: tableData.pagination.pageSize ?? 5 }, newFilters);
        }}
      />
      <BaseTable
        columns={columns}
        dataSource={tableData.data}
        pagination={{
          current: tableData.pagination.current,
          pageSize: tableData.pagination.pageSize,
          total: tableData.pagination.total,
          showTotal,
        }}
        loading={tableData.loading}
        onChange={handleTableChange}
        scroll={{ x: true, scrollToFirstRowOnChange: true }}
      />
    </BaseCard>
  );
};

export default LabTaskTable;
